/**
 * The shed designer, as a film shows it: a monitor on a desk with the configurator open,
 * a pointer working its controls, the buy button, and the plan coming off the printer into
 * the builder's arms.
 *
 * Every film that sells a shed has this scene, and what changes between them is what the
 * shopper chooses: so a film hands in a `DesignScript` — which controls the panel shows
 * and what they read at each moment, where the pointer goes, when it clicks — and the
 * room, the monitor, the printer and the builder are drawn here, once. The controls are
 * labeled as the configurator labels them (`ui/fields.ts`), because the film is showing
 * the product, and the numbers on them are the shopper's own choices, never a plan's.
 */

import { SHED_DESIGNER_PATH } from "../site/paths.js";
import type { Site, View } from "./film.js";
import type { Ink } from "./ink.js";
import type { Pt } from "./ink.js";
import { cursor, planSheet, printer, shoot, sparkle, type Camera } from "./props.js";
import { KNEEL, STAND, blend, drawFigure, joints, type Figure, type Pose } from "./rig.js";
import { BUILT, TEN_BY_TWELVE, drawShed, type Shape } from "./shed.js";
import type { Mix } from "./sound.js";
import { along, clamp, cueAt, ease, mix, pulse, span, type Key } from "./timing.js";

// ---- the screen ----------------------------------------------------------------------------

/** A slider: a value between two ends, shown in whole feet. */
export interface Slider {
  kind: "slider";
  label: string;
  value: number;
  min: number;
  max: number;
}

/** A segmented control: one of a few options, the chosen one filled. */
export interface Choice {
  kind: "choice";
  label: string;
  options: readonly string[];
  chosen: number;
}

/** A row of buttons under a heading, as the configurator adds a door or a window. */
export interface Buttons {
  kind: "buttons";
  label: string;
  buttons: readonly string[];
  /** The one being pressed, and how far down it is (0–1). */
  pressed: number;
  press: number;
}

export type Control = Slider | Choice | Buttons;

/** What the designer shows at one moment. */
export interface Screenful {
  /** The panel's controls, top to bottom. */
  controls: readonly Control[];
  /** The footprint the view's caption gives, in feet. */
  width: number;
  length: number;
  /** The building in the view. */
  shape: Shape;
  /** The buy button, from rest (0) to pressed (1). */
  press: number;
  /** From not yet paid (0) to paid (1). */
  paid: number;
}

/** Where the monitor's screen is, in world units. */
export const SCREEN = { x: 30, y: 104, w: 150, h: 96 };
const PANEL_X = SCREEN.x + 4;
const PANEL_W = 54;
const TRACK_X = PANEL_X + 4;
const TRACK_W = PANEL_W - 14;

/** The buy button's middle, for a pointer to find it. */
export const BUY: Pt = { x: PANEL_X + PANEL_W / 2, y: SCREEN.y + 85 };

/**
 * The top of the control in `row` of a panel of `count`: spread evenly from the first
 * row's place to the last's, to whole units, so four controls sit where they always have.
 */
function rowY(count: number, row: number): number {
  const first = 30;
  const last = 70;
  return SCREEN.y + (count === 1 ? first : first + Math.round((row * (last - first)) / (count - 1)));
}

function control<K extends Control["kind"]>(controls: readonly Control[], row: number, kind: K): Extract<Control, { kind: K }> {
  const found = controls[row];
  if (found?.kind !== kind) {
    throw new Error(`the designer's row ${row} is not a ${kind}`);
  }
  return found as Extract<Control, { kind: K }>;
}

/** Where a slider's knob sits at `value`, for a pointer to grab it. */
export function knob(controls: readonly Control[], row: number, value: number): Pt {
  const slider = control(controls, row, "slider");
  const k = clamp((value - slider.min) / (slider.max - slider.min));
  return { x: TRACK_X + TRACK_W * k, y: rowY(controls.length, row) + 4 };
}

/** The middle of a segmented control's option, for a pointer to click it. */
export function option(controls: readonly Control[], row: number, index: number): Pt {
  const choice = control(controls, row, "choice");
  const w = PANEL_W - 4;
  return { x: PANEL_X + 2 + (w / choice.options.length) * (index + 0.5), y: rowY(controls.length, row) + 4.5 };
}

/** The middle of one of a row's buttons, for a pointer to press it. */
export function button(controls: readonly Control[], row: number, index: number): Pt {
  const buttons = control(controls, row, "buttons");
  const w = PANEL_W - 4;
  return { x: PANEL_X + 2 + (w / buttons.buttons.length) * (index + 0.5), y: rowY(controls.length, row) + 4.5 };
}

/** A monitor on a stand, with the configurator open on it. */
export function monitor(ink: Ink, screen: Screenful, site: Site, drawShedInto: (x: number, y: number, scale: number) => void): void {
  const p = ink.palette;
  const { x, y, w, h } = SCREEN;
  // Stand and bezel.
  ink.shape(
    [
      { x: x + w / 2 - 10, y: y + h + 4 },
      { x: x + w / 2 + 10, y: y + h + 4 },
      { x: x + w / 2 + 14, y: y + h + 26 },
      { x: x + w / 2 - 14, y: y + h + 26 },
    ],
    { fill: p.roof, width: 2.4 },
  );
  ink.rect(x + w / 2 - 34, y + h + 24, 68, 6, { fill: p.roof, width: 2.4, offset: 0 });
  ink.rect(x - 6, y - 6, w + 12, h + 12, { fill: p.ink, width: 2.6, offset: 0 });
  ink.rect(x, y, w, h, { fill: p.paper, width: 1.2, offset: 0, overshoot: false });

  // The browser's bar and the address.
  ink.rect(x, y, w, 9, { fill: p.shadow, width: 0.8, offset: 0, overshoot: false });
  for (const [i, color] of [p.signal, p.accent, p.grass].entries()) {
    ink.dot(x + 4 + i * 4, y + 4.5, 1.3, color);
  }
  ink.rect(x + 18, y + 2, w - 24, 5, { fill: p.paper, width: 0.6, offset: 0, overshoot: false });
  ink.text(`${site.host}${SHED_DESIGNER_PATH}`, x + 21, y + 4.6, { size: 3.4, weight: 500, align: "left" });

  // The panel of controls.
  ink.text("Design your shed", PANEL_X, y + 16, { size: 5, weight: 800, align: "left" });
  const count = screen.controls.length;
  screen.controls.forEach((each, row) => {
    const top = rowY(count, row);
    if (each.kind === "slider") {
      ink.text(each.label, PANEL_X, top, { size: 3.6, weight: 600, align: "left" });
      ink.text(`${Math.round(each.value)}'`, PANEL_X + PANEL_W - 2, top, { size: 3.6, weight: 800, align: "right" });
      ink.line(TRACK_X, top + 4, TRACK_X + TRACK_W, top + 4, { width: 1.6, color: p.shadow, overshoot: false });
      const at = knob(screen.controls, row, each.value);
      ink.line(TRACK_X, top + 4, at.x, at.y, { width: 1.6, color: p.accent, overshoot: false });
      ink.circle(at.x, at.y, 2.4, { fill: p.accent, width: 0.8, offset: 0 });
      return;
    }
    ink.text(each.label, PANEL_X, top - 1, { size: 3.6, weight: 600, align: "left" });
    if (each.kind === "buttons") {
      const bw = (PANEL_W - 4) / each.buttons.length;
      each.buttons.forEach((label, i) => {
        const down = i === each.pressed ? each.press : 0;
        const bx = PANEL_X + 2 + bw * i + 0.8;
        ink.rect(bx, top + 2 + down * 0.6, bw - 1.6, 5, { fill: down > 0 ? p.shadow : p.paper, width: 0.7, offset: 0, overshoot: false });
        ink.text(label, bx + (bw - 1.6) / 2, top + 4.6 + down * 0.6, { size: 2.9, weight: 700 });
      });
      return;
    }
    const cw = (PANEL_W - 4) / each.options.length;
    each.options.forEach((label, i) => {
      const cx = PANEL_X + 2 + cw * i;
      const chosen = i === each.chosen;
      ink.rect(cx, top + 2, cw, 5, { fill: chosen ? p.ink : p.paper, width: 0.6, offset: 0, overshoot: false });
      ink.text(label, cx + cw / 2, top + 4.6, { size: 2.9, weight: 700, color: chosen ? p.paper : p.ink });
    });
  });

  const press = screen.press;
  const bx = PANEL_X + 1;
  const by = BUY.y - 4 + press * 0.8;
  ink.rect(bx, by, PANEL_W - 2, 8, { fill: screen.paid > 0 ? p.grass : p.accent, width: 0.9, offset: press > 0 ? 0 : 0.8 });
  ink.text(screen.paid > 0 ? "Paid ✓" : `Buy plans — ${site.price}`, bx + (PANEL_W - 2) / 2, by + 4.2, { size: 3.6, weight: 800 });

  // The view: a patch of sky and grass with the shed standing in it.
  const vx = x + PANEL_W + 10;
  const vw = w - PANEL_W - 14;
  ink.wash(
    [
      { x: vx, y: y + 12 },
      { x: vx + vw, y: y + 12 },
      { x: vx + vw, y: y + 70 },
      { x: vx, y: y + 70 },
    ],
    p.sky,
    0.8,
    0,
  );
  ink.wash(
    [
      { x: vx, y: y + 70 },
      { x: vx + vw, y: y + 70 },
      { x: vx + vw, y: y + h - 4 },
      { x: vx, y: y + h - 4 },
    ],
    p.grass,
    0.9,
    0,
  );
  drawShedInto(vx + 14, y + 76, 3.2);
  ink.text(`${Math.round(screen.width)}' × ${Math.round(screen.length)}'`, vx + vw / 2, y + h - 8, { size: 5, weight: 800 });
}

// ---- the scene -----------------------------------------------------------------------------

/** The moments of the scene that every design script shares, in its own seconds. */
export interface DesignTiming {
  buyClick: number;
  /** From the builder smiling at the screen on. */
  delighted: number;
  /** The camera pulling back from the screen to find the builder. */
  pullBack: readonly [number, number];
  printing: readonly [number, number];
  /** The plan's pages: each comes out of the printer `step` after the last, then flies. */
  pages: { out: number; step: number; fly: readonly [number, number] };
  /** Up off the stool to catch them. */
  stand: readonly [number, number];
  /** And a hop for joy. */
  hop: readonly [number, number];
}

/** One film's run at the designer: what it shows, where the pointer goes, when it clicks. */
export interface DesignScript {
  at(u: number): Screenful;
  /** The pointer's path: where it is at each key, eased between. */
  keys: readonly Key[];
  /** Every moment the button goes down, the buy click among them. */
  clicks: readonly number[];
  timing: DesignTiming;
}

/** The plan's pages, as they come off the printer. */
const PAGES = ["FRAMING", "CUT LIST", "GUIDE"];

export function designScene(ink: Ink, view: View, u: number, site: Site, script: DesignScript): void {
  const screen = script.at(u);
  const timing = script.timing;
  const pointer = along(script.keys, u);
  const lastClick = script.clicks[cueAt(u, script.clicks)] ?? -10;
  const click = span(u, lastClick, lastClick + 0.3);

  const back = ease.inOut(span(u, ...timing.pullBack));
  const screenCenter = { x: SCREEN.x + SCREEN.w / 2, y: SCREEN.y + SCREEN.h / 2 };
  const camera: Camera = {
    x: mix(screenCenter.x, 112, back),
    y: mix(screenCenter.y + 4, 196, back),
    zoom: mix(5.3, 2.05, back),
  };

  shoot(
    ink,
    camera,
    (v) => {
      // A room: the wall, the floor, a desk.
      const floor = 306;
      ink.wash(
        [
          { x: v.left - 20, y: floor },
          { x: v.right + 20, y: floor },
          { x: v.right + 20, y: v.bottom + 20 },
          { x: v.left - 20, y: v.bottom + 20 },
        ],
        ink.palette.wood,
        0.45,
        0,
      );
      ink.line(v.left - 20, floor, v.right + 20, floor, { width: 2.6 });
      ink.rect(-200, 232, 560, 12, { fill: ink.palette.woodDark, width: 2.6 });
      for (const lx of [-186, 334]) {
        ink.line(lx, 244, lx, floor, { width: 4, overshoot: false });
      }
      // A picture on the wall: a shed, of course.
      ink.rect(-150, 40, 90, 70, { fill: ink.palette.trim, width: 2.6 });
      drawShed(ink, { x: -136, y: 98, scale: 3 }, TEN_BY_TWELVE, BUILT);

      monitor(ink, screen, site, (x, y, scale) => {
        drawShed(ink, { x, y, scale }, screen.shape, BUILT);
      });
      ink.rect(8, 224, 92, 7, { fill: ink.palette.roof, width: 1.8, offset: 0 });
      ink.ellipse(118, 227, 8, 4, { fill: ink.palette.roof, width: 1.6, offset: 0 });

      // Sparks off the buy button.
      const burst = span(u, timing.buyClick, timing.buyClick + 0.55);
      if (burst > 0 && burst < 1) {
        for (let i = 0; i < 7; i += 1) {
          const a = -Math.PI / 2 + (i - 3) * 0.42;
          const r = 6 + burst * 26;
          sparkle(ink, BUY.x + Math.cos(a) * r, BUY.y + Math.sin(a) * r * 0.8, burst, 4);
        }
      }

      // The printer, and the plan coming off it into the builder's arms.
      printer(ink, 290, 232, span(u, ...timing.printing) > 0 && u < timing.printing[1] ? u : 0);
      const stood = ease.inOut(span(u, ...timing.stand));
      const builderX = mix(-38, -60, stood);
      const sitPose: Pose = { ...KNEEL, lean: 0.12, nod: 0.1, legA: 1.5, kneeA: -1.5, legB: 1.35, kneeB: -1.45 };
      const hug: Pose = { ...STAND, lean: -0.05, armA: 1.35, elbowA: 1.15, armB: 1.2, elbowB: 1.2 };
      const pose = blend(sitPose, hug, stood);
      const hop = pulse(u, ...timing.hop) * 18;
      const figure: Figure = {
        x: builderX,
        ground: stood > 0.5 ? floor - hop : undefined,
        y: stood > 0.5 ? undefined : 262,
        facing: 1,
        eyes: u > timing.delighted ? "happy" : "open",
        mouth: u > timing.delighted ? "grin" : "smile",
        look: stood > 0.5 ? 0.25 : 0.8,
        reachA: stood < 0.2 ? { x: 40, y: 226 } : undefined,
        reachB: stood < 0.2 ? { x: 18, y: 226 } : undefined,
      };
      // The stool.
      ink.rect(-72, 262, 50, 8, { fill: ink.palette.woodDark, width: 2.2 });
      ink.line(-66, 270, -70, floor, { width: 3, overshoot: false });
      ink.line(-28, 270, -24, floor, { width: 3, overshoot: false });
      // Not in the close-up of the screen: the camera pulls back to find them.
      const at = back > 0 ? drawFigure(ink, pose, figure) : joints(pose, figure);
      PAGES.forEach((title, i) => {
        const { out: first, step, fly: flight } = timing.pages;
        const out = span(u, first + i * step, first + 0.15 + i * step);
        if (out <= 0) {
          return;
        }
        const fly = ease.inOut(span(u, first + flight[0] + i * step, first + flight[1] + i * step));
        const from = { x: 290, y: 200 - out * 30 };
        const to = { x: at.handA.x + 6 - i * 3, y: at.handA.y - 14 - i * 4 };
        const x = mix(from.x, to.x, fly);
        const y = mix(from.y, to.y, fly) - Math.sin(fly * Math.PI) * 70;
        planSheet(ink, x, y, mix(0, -0.15 + i * 0.12, fly) + Math.sin(fly * Math.PI) * 1.4, title, mix(0.7, 1, fly));
      });
      if (back < 0.5) {
        cursor(ink, pointer.x, pointer.y, click);
      }
    },
    view,
  );
}

// ---- its sound -----------------------------------------------------------------------------

/**
 * Bought, and printed: the click, the register, a shimmer off the button, the printer and
 * the pages flying. `pan` is where the screen is in the frame.
 */
export function designSound(mix: Mix, at: number, timing: DesignTiming, pan: number): void {
  const bought = at + timing.buyClick;
  mix.click(bought, { gain: 1, pan });
  mix.slap(bought + 0.06, { gain: 0.25, pan });
  mix.bell(bought + 0.08, 2093, { gain: 0.6, pan, decay: 0.9 });
  mix.bell(bought + 0.15, 2637, { gain: 0.55, pan, decay: 1.1 });
  [96, 100, 103].forEach((note, i) => mix.glock(bought + 0.2 + i * 0.07, note, 0.3, pan));

  const [prints, printed] = timing.printing;
  mix.whirr(at + prints, printed - prints, { gain: 0.8, pan: 0.45 });
  const { out, step, fly } = timing.pages;
  for (let i = 0; i < PAGES.length; i += 1) {
    mix.flutter(at + out + i * step, 0.12, { gain: 0.5, pan: 0.45 });
    mix.flutter(at + out + fly[0] + i * step, fly[1] - fly[0], { gain: 0.6, pan: 0.05 });
  }
}
